import { useState } from 'react';
import { 
  BookOpen, 
  Search, 
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FileText,
  AlertCircle,
  CheckCircle2,
  Info
} from 'lucide-react';

interface CodeSection {
  id: string;
  code: string;
  chapter: string;
  section: string;
  title: string;
  content: string;
  subsections?: CodeSubsection[];
  importance: 'critical' | 'important' | 'reference';
}

interface CodeSubsection {
  id: string;
  number: string;
  title: string;
  content: string;
}

const codeReferences: CodeSection[] = [
  {
    id: 'nfpa72-ch10',
    code: 'NFPA 72',
    chapter: '10',
    section: '10.4',
    title: 'Spacing of Smoke Detectors',
    content: 'Smoke detectors shall be located on the ceiling or, if on a sidewall, between the ceiling and 12 in. (300 mm) down from the ceiling to the top of the detector.',
    importance: 'critical',
    subsections: [
      {
        id: 'nfpa72-10.4.1',
        number: '10.4.1',
        title: 'Smooth Ceiling Spacing',
        content: 'Smoke detectors on smooth ceilings shall have a maximum spacing of 30 ft (9.1 m) between detectors.'
      },
      {
        id: 'nfpa72-10.4.2',
        number: '10.4.2',
        title: 'Beam Construction',
        content: 'For beam construction, spacing shall be reduced based on beam depth and spacing.'
      }
    ]
  },
  {
    id: 'nfpa72-ch18',
    code: 'NFPA 72',
    chapter: '18',
    section: '18.4',
    title: 'Notification Appliance Audibility',
    content: 'Public mode audible notification appliances shall produce signals at least 15 dB above the average ambient sound level or 5 dB above the maximum sound level having a duration of at least 60 seconds, whichever is greater.',
    importance: 'critical',
    subsections: [
      {
        id: 'nfpa72-18.4.1',
        number: '18.4.1',
        title: 'Sleeping Areas',
        content: 'In sleeping areas, the audible notification appliance shall have a sound level of at least 75 dBA at the pillow level.'
      },
      {
        id: 'nfpa72-18.4.2',
        number: '18.4.2',
        title: 'Public Mode',
        content: 'The sound level shall not exceed 110 dBA at the minimum hearing distance from the audible appliance.'
      }
    ]
  },
  {
    id: 'nfpa72-ch23',
    code: 'NFPA 72',
    chapter: '23',
    section: '23.8',
    title: 'Secondary Power Supply',
    content: 'A secondary power supply shall be provided and shall be capable of operating the system under quiescent load for a minimum of 24 hours and, at the end of that period, shall be capable of operating all alarm notification appliances for 5 minutes.',
    importance: 'critical',
    subsections: [
      {
        id: 'nfpa72-23.8.1',
        number: '23.8.1',
        title: 'Battery Calculations',
        content: 'Battery capacity shall be calculated based on the standby current draw plus alarm current requirements.'
      }
    ]
  },
  {
    id: 'nfpa72-ch17',
    code: 'NFPA 72',
    chapter: '17',
    section: '17.7',
    title: 'Initiating Device Circuits',
    content: 'Initiating device circuits shall be installed such that a single open or a ground fault on the circuit conductors will not interfere with the normal operation of other circuits.',
    importance: 'important',
    subsections: [
      {
        id: 'nfpa72-17.7.1',
        number: '17.7.1',
        title: 'Class A Circuits',
        content: 'Class A circuits shall be arranged so that a single open does not prevent alarm operation.'
      },
      {
        id: 'nfpa72-17.7.2',
        number: '17.7.2',
        title: 'Class B Circuits',
        content: 'Class B circuits shall be supervised for integrity.'
      }
    ]
  },
  {
    id: 'nfpa72-ch14',
    code: 'NFPA 72',
    chapter: '14',
    section: '14.3',
    title: 'Inspection, Testing, and Maintenance',
    content: 'All systems shall be inspected, tested, and maintained in accordance with the schedules in Table 14.3.1 or more frequently if required by the authority having jurisdiction.',
    importance: 'important',
    subsections: [
      {
        id: 'nfpa72-14.3.1',
        number: '14.3.1',
        title: 'Annual Testing',
        content: 'Fire alarm systems shall be tested annually at minimum.'
      }
    ]
  },
  {
    id: 'ibc-907',
    code: 'IBC',
    chapter: '9',
    section: '907.2',
    title: 'Where Required',
    content: 'An approved fire alarm system installed in accordance with the provisions of this code and NFPA 72 shall be provided in new buildings and structures.',
    importance: 'critical',
    subsections: [
      {
        id: 'ibc-907.2.1',
        number: '907.2.1',
        title: 'Group A',
        content: 'A manual fire alarm system shall be installed in Group A occupancies with an occupant load of 300 or more.'
      },
      {
        id: 'ibc-907.2.3',
        number: '907.2.3',
        title: 'Group E',
        content: 'A manual fire alarm system shall be installed in Group E educational occupancies.'
      },
      {
        id: 'ibc-907.2.6',
        number: '907.2.6',
        title: 'Group I',
        content: 'An automatic smoke detection system shall be installed throughout Group I-1 and I-2 occupancies.'
      }
    ]
  },
  {
    id: 'nec-760',
    code: 'NEC',
    chapter: '7',
    section: '760.41',
    title: 'PLFA Circuit Power Source Requirements',
    content: 'Power-limited fire alarm (PLFA) circuit power sources shall be listed for the purpose and shall comply with the requirements of 760.41(A) and (B).',
    importance: 'important',
    subsections: [
      {
        id: 'nec-760.41a',
        number: '760.41(A)',
        title: 'Transformers',
        content: 'Transformers shall comply with UL 1585 or UL 1950.'
      },
      {
        id: 'nec-760.41b',
        number: '760.41(B)',
        title: 'Power Supplies',
        content: 'Power supplies shall be listed and marked as suitable for fire alarm use.'
      }
    ]
  },
  {
    id: 'nfpa72-ch21',
    code: 'NFPA 72',
    chapter: '21',
    section: '21.3',
    title: 'Emergency Voice/Alarm Communications',
    content: 'Emergency voice/alarm communications systems shall be capable of transmitting voice messages to designated areas.',
    importance: 'reference',
    subsections: [
      {
        id: 'nfpa72-21.3.1',
        number: '21.3.1',
        title: 'Intelligibility',
        content: 'Voice messages shall be intelligible in accordance with Section 18.4.10.'
      }
    ]
  },
  {
    id: 'nfpa72-ch24',
    code: 'NFPA 72',
    chapter: '24',
    section: '24.3',
    title: 'Emergency Control Functions',
    content: 'Emergency control function interfaces shall be installed, tested, and maintained in accordance with the requirements of this chapter.',
    importance: 'reference',
    subsections: [
      {
        id: 'nfpa72-24.3.1',
        number: '24.3.1',
        title: 'Door Release',
        content: 'Fire alarm systems shall be capable of releasing doors held open by magnetic hold-open devices.'
      },
      {
        id: 'nfpa72-24.3.2',
        number: '24.3.2',
        title: 'Elevator Recall',
        content: 'Fire alarm systems shall initiate elevator recall in accordance with ASME A17.1.'
      }
    ]
  }
];

export function CodeReferencesPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [selectedCode, setSelectedCode] = useState('all');

  const toggleSection = (id: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedSections(newExpanded);
  };

  const filteredReferences = codeReferences.filter(ref => {
    const matchesSearch = 
      ref.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ref.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ref.section.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCode = selectedCode === 'all' || ref.code === selectedCode;
    
    return matchesSearch && matchesCode;
  });

  const getImportanceColor = (importance: string) => {
    switch (importance) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'important': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'reference': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-slate-600 bg-slate-50 border-slate-200';
    }
  };

  const getImportanceIcon = (importance: string) => {
    switch (importance) {
      case 'critical': return <AlertCircle className="w-4 h-4" />;
      case 'important': return <Info className="w-4 h-4" />;
      case 'reference': return <FileText className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <BookOpen className="w-16 h-16 mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Code References</h1>
            <p className="text-xl text-blue-100">
              Quick reference guide to NFPA 72, IBC, IFC, and NEC requirements
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Search and Filter */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Search Code Sections
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by section, title, or content..."
                  className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Filter by Code
              </label>
              <select
                value={selectedCode}
                onChange={(e) => setSelectedCode(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Codes</option>
                <option value="NFPA 72">NFPA 72</option>
                <option value="IBC">IBC</option>
                <option value="IFC">IFC</option>
                <option value="NEC">NEC</option>
              </select>
            </div>
          </div>

          <div className="mt-4 text-sm text-slate-600">
            Showing {filteredReferences.length} of {codeReferences.length} code sections
          </div>
        </div>

        {/* Legend */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Importance Legend</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
              <div>
                <span className="font-semibold text-red-600">Critical</span>
                <p className="text-xs text-slate-600">Must comply for code approval</p>
              </div>
            </div>
            <div className="flex items-center">
              <Info className="w-5 h-5 text-orange-600 mr-2" />
              <div>
                <span className="font-semibold text-orange-600">Important</span>
                <p className="text-xs text-slate-600">Significant design consideration</p>
              </div>
            </div>
            <div className="flex items-center">
              <FileText className="w-5 h-5 text-blue-600 mr-2" />
              <div>
                <span className="font-semibold text-blue-600">Reference</span>
                <p className="text-xs text-slate-600">Additional information</p>
              </div>
            </div>
          </div>
        </div>

        {/* Code Sections */}
        <div className="space-y-4">
          {filteredReferences.map(ref => (
            <div key={ref.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
              {/* Section Header */}
              <div
                className="p-6 cursor-pointer hover:bg-slate-50 transition-colors duration-200"
                onClick={() => toggleSection(ref.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-bold rounded mr-3">
                        {ref.code} {ref.section}
                      </span>
                      <span className={`px-3 py-1 text-xs font-semibold rounded border flex items-center ${getImportanceColor(ref.importance)}`}>
                        {getImportanceIcon(ref.importance)}
                        <span className="ml-1 capitalize">{ref.importance}</span>
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">
                      {ref.title}
                    </h3>
                    <p className="text-slate-700">
                      {ref.content}
                    </p>
                  </div>
                  <div className="ml-4">
                    {expandedSections.has(ref.id) ? (
                      <ChevronDown className="w-6 h-6 text-slate-400" />
                    ) : (
                      <ChevronRight className="w-6 h-6 text-slate-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Subsections */}
              {expandedSections.has(ref.id) && ref.subsections && (
                <div className="border-t border-slate-200 bg-slate-50 p-6">
                  <h4 className="text-lg font-bold text-slate-900 mb-4">Subsections</h4>
                  <div className="space-y-4">
                    {ref.subsections.map(sub => (
                      <div key={sub.id} className="bg-white rounded-lg p-4 border border-slate-200">
                        <div className="flex items-start">
                          <CheckCircle2 className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                          <div>
                            <h5 className="font-bold text-slate-900 mb-1">
                              {sub.number} - {sub.title}
                            </h5>
                            <p className="text-sm text-slate-700">
                              {sub.content}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredReferences.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No code sections found</h3>
            <p className="text-slate-500">Try adjusting your search or filters</p>
          </div>
        )}

        {/* External Resources */}
        <div className="mt-12 bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold mb-6">Official Code Resources</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <a
              href="https://www.nfpa.org/codes-and-standards/all-codes-and-standards/list-of-codes-and-standards/detail?code=72"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-4 bg-white/10 backdrop-blur-sm rounded-lg hover:bg-white/20 transition-colors duration-200"
            >
              <span className="font-semibold">NFPA 72 Official Site</span>
              <ExternalLink className="w-5 h-5" />
            </a>
            <a
              href="https://codes.iccsafe.org/content/IBC2021P1"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-4 bg-white/10 backdrop-blur-sm rounded-lg hover:bg-white/20 transition-colors duration-200"
            >
              <span className="font-semibold">International Building Code</span>
              <ExternalLink className="w-5 h-5" />
            </a>
            <a
              href="https://codes.iccsafe.org/content/IFC2021P1"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-4 bg-white/10 backdrop-blur-sm rounded-lg hover:bg-white/20 transition-colors duration-200"
            >
              <span className="font-semibold">International Fire Code</span>
              <ExternalLink className="w-5 h-5" />
            </a>
            <a
              href="https://www.nfpa.org/codes-and-standards/all-codes-and-standards/list-of-codes-and-standards/detail?code=70"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-4 bg-white/10 backdrop-blur-sm rounded-lg hover:bg-white/20 transition-colors duration-200"
            >
              <span className="font-semibold">National Electrical Code (NEC)</span>
              <ExternalLink className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
